import type { AnimalSex, LifecycleState } from "./types";

export const LIFECYCLE_STATE_VALUES = [
  "registered",
  "calf_lactating",
  "calf_weaned",
  "heifer_young",
  "heifer_developing",
  "heifer_served",
  "heifer_pregnant",
  "cow_producing",
  "male_fattening",
  "bull",
  "culled_for_sale",
  "dead",
  "sold",
] as const satisfies readonly LifecycleState[];

export type LifecycleGroup = "cria" | "desarrollo" | "productiva" | "retirada";

export const LIFECYCLE_GROUP: Record<LifecycleState, LifecycleGroup> = {
  registered: "cria",
  calf_lactating: "cria",
  calf_weaned: "cria",
  heifer_young: "desarrollo",
  heifer_developing: "desarrollo",
  heifer_served: "desarrollo",
  heifer_pregnant: "desarrollo",
  cow_producing: "productiva",
  male_fattening: "productiva",
  bull: "productiva",
  culled_for_sale: "retirada",
  sold: "retirada",
  dead: "retirada",
};

export const LIFECYCLE_LABEL: Record<LifecycleState, string> = {
  registered: "Registrado",
  calf_lactating: "Ternero lactante",
  calf_weaned: "Ternero destetado",
  heifer_young: "Novilla joven",
  heifer_developing: "Novilla en desarrollo",
  heifer_served: "Novilla servida",
  heifer_pregnant: "Novilla preñada",
  cow_producing: "Vaca en producción",
  male_fattening: "Macho en engorde",
  bull: "Toro",
  culled_for_sale: "Descarte",
  sold: "Vendido",
  dead: "Fallecido",
};

// B85: las dos etapas de cría se feminizan cuando el sexo del animal es hembra.
// Un finquero CR nota la incoherencia "lote Terneras" vs estado "Ternero"; el
// resto de los estados ya tiene género implícito (novilla/vaca = hembra;
// toro/macho = macho) y no varía. Para filtros/dropdowns genéricos (sin sexo
// conocido) se usa el label neutro de LIFECYCLE_LABEL.
const CALF_FEMALE_LABEL: Partial<Record<LifecycleState, string>> = {
  calf_lactating: "Ternera lactante",
  calf_weaned: "Ternera destetada",
};

// Label del estado consciente del sexo. Pasar `sex` solo en la fila/ficha del
// animal, donde se conoce; sin `sex` devuelve el label neutro (dropdowns).
export function lifecycleLabel(state: LifecycleState, sex?: AnimalSex): string {
  if (sex === "female") {
    const feminine = CALF_FEMALE_LABEL[state];
    if (feminine) return feminine;
  }
  return LIFECYCLE_LABEL[state];
}

export const LIFECYCLE_GROUP_LABEL: Record<LifecycleGroup, string> = {
  cria: "Cría",
  desarrollo: "Desarrollo",
  productiva: "Productiva",
  retirada: "Retirada",
};

export const LIFECYCLE_STATES_BY_GROUP: Record<LifecycleGroup, LifecycleState[]> =
  LIFECYCLE_STATE_VALUES.reduce(
    (acc, state) => {
      const group = LIFECYCLE_GROUP[state];
      acc[group].push(state);
      return acc;
    },
    {
      cria: [] as LifecycleState[],
      desarrollo: [] as LifecycleState[],
      productiva: [] as LifecycleState[],
      retirada: [] as LifecycleState[],
    },
  );

// MANTENER SINCRONIZADO con src/features/animals/schemas.ts:INITIAL_LIFECYCLE_STATES.
// Espejado acá para evitar arrastrar zod + timezone util al bundle del browser.
// El drift se enforza en lifecycleStates.test.ts contra @server/features/animals/schemas.
export const INITIAL_LIFECYCLE_STATES = [
  "registered",
  "calf_lactating",
  "calf_weaned",
  "heifer_young",
  "heifer_developing",
  "male_fattening",
] as const satisfies readonly LifecycleState[];

export type InitialLifecycleState = (typeof INITIAL_LIFECYCLE_STATES)[number];

export const INITIAL_LIFECYCLE_STATES_BY_GROUP: Record<LifecycleGroup, LifecycleState[]> =
  INITIAL_LIFECYCLE_STATES.reduce(
    (acc, state) => {
      acc[LIFECYCLE_GROUP[state]].push(state);
      return acc;
    },
    {
      cria: [] as LifecycleState[],
      desarrollo: [] as LifecycleState[],
      productiva: [] as LifecycleState[],
      retirada: [] as LifecycleState[],
    },
  );

// ─── Lot stages ──────────────────────────────────────────────────────────────
// Subset of lifecycle states that lots can hold. Excludes "registered" (estado
// transitorio post-import) y los terminales (sold/dead/culled_for_sale).
// Espejo del CHECK constraint `lots_stage_check` en src/db/schema.ts. Hay un
// test que verifica el match exacto contra el constraint para prevenir drift.

export type LotStage =
  | "calf_lactating"
  | "calf_weaned"
  | "heifer_young"
  | "heifer_developing"
  | "heifer_served"
  | "heifer_pregnant"
  | "cow_producing"
  | "male_fattening"
  | "bull";

export type LotStageGroup = "cria" | "desarrollo" | "productiva";

export const LOT_STAGE_GROUP_ORDER: LotStageGroup[] = ["cria", "desarrollo", "productiva"];

export const LOT_STAGES_BY_GROUP: Record<LotStageGroup, LotStage[]> = {
  cria: LIFECYCLE_STATES_BY_GROUP.cria.filter((s): s is LotStage => s !== "registered"),
  desarrollo: LIFECYCLE_STATES_BY_GROUP.desarrollo as LotStage[],
  productiva: LIFECYCLE_STATES_BY_GROUP.productiva as LotStage[],
};
